import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { queryClient } from "../queries/client";
import { refetchAccessToken } from "./idServer";
import { useAuthStore } from "../store";
import optionsApi from "./optionsApi";

const idServerApi = axios.create({
  baseURL:
    process.env.REACT_APP_ID_SERVER_URL ||
    "https://identity-api.dev.hissa.com/v1/",
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

function addRefreshSubscriber(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

optionsApi.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig;
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      (error.response?.data as any)?.errorRaw?.name === "TokenExpiredError"
    ) {
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newToken = await refetchAccessToken();
          useAuthStore.setState((state) => ({
            ...state,
            accessToken: newToken,
          })); // Use Zustand store outside React
          onTokenRefreshed(newToken);
          isRefreshing = false;
          return optionsApi(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          queryClient.clear();
          return Promise.reject(refreshError);
        }
      }

      return new Promise((resolve) => {
        addRefreshSubscriber((token) => {
          if (originalRequest && originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          resolve(optionsApi(originalRequest));
        });
      });
    }

    return Promise.reject(error);
  }
);

export default idServerApi;
